import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Navigation, Loader2, ArrowRight } from 'lucide-react';
import { useLocations } from '../happyspace-restaurant-sdk';
import { Helmet } from 'react-helmet-async';

export default function LocationsPage() {
  const { locations, nearestLocation, loading, error } = useLocations();

  if (loading) {
    return (
      <div className="container mx-auto py-24 px-4">
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#D97706]" />
          <p className="text-gray-500">Loading locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-24 px-4">
        <div className="max-w-md mx-auto text-center bg-white rounded-2xl p-8 shadow-lg border border-orange-100">
          <MapPin className="w-12 h-12 text-[#D97706] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#451A03] mb-2">Unable to load locations</h2>
          <p className="text-gray-500 mb-6">{error?.message || 'Please try again later.'}</p>
          <button onClick={() => window.location.reload()} className="bg-[#D97706] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#B45309] transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 px-4 pb-24">
      <Helmet>
        <title>Our Locations | Kebabs to go!</title>
        <meta name="description" content="Find Kebabs to go! locations. Order online for pickup or delivery." />
      </Helmet>

      <div className="mb-12">
        <h1 className="text-3xl font-bold text-[#451A03] mb-2">Our Locations</h1>
        <p className="text-gray-500">
          {locations.length > 0
            ? `Find us at ${locations.length} location${locations.length === 1 ? '' : 's'}. Nearest to you shown first.`
            : 'Check back soon for our locations.'}
        </p>
      </div>

      {locations.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-orange-100">
          <MapPin className="w-16 h-16 text-[#D97706] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#451A03] mb-2">No locations yet</h2>
          <p className="text-gray-500 mb-6">Check back soon for our locations.</p>
          <Link to="/menu" className="inline-flex items-center gap-2 bg-[#D97706] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#B45309] transition-colors">
            Order Online <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((loc, i) => {
            const isNearest = nearestLocation && (loc._id === nearestLocation._id || loc.name === nearestLocation.name);
            return (
              <div
                key={loc._id || i}
                className={`bg-white p-8 rounded-2xl shadow-sm border-2 transition-colors ${
                  isNearest ? 'border-[#D97706] ring-2 ring-[#D97706]/20' : 'border-orange-100'
                }`}
              >
                {isNearest && (
                  <span className="inline-block px-3 py-1 bg-[#D97706] text-white text-xs font-bold rounded-full mb-4">Nearest to you</span>
                )}
                <h3 className="text-xl font-bold text-[#451A03] mb-4">{loc.name}</h3>
                <div className="space-y-3">
                  {loc.address && (
                    <div className="flex gap-3">
                      <MapPin className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                      <p className="text-gray-600 text-sm">{loc.address}</p>
                    </div>
                  )}
                  {loc.phone && (
                    <div className="flex gap-3">
                      <Phone className="w-5 h-5 text-[#D97706] flex-shrink-0" />
                      <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="text-gray-600 text-sm font-medium hover:text-[#D97706]">
                        {loc.phone}
                      </a>
                    </div>
                  )}
                  {loc.hours && (
                    <div className="flex gap-3">
                      <Clock className="w-5 h-5 text-[#D97706] flex-shrink-0" />
                      <p className="text-gray-600 text-sm">{loc.hours}</p>
                    </div>
                  )}
                  {loc.distance != null && (
                    <p className="text-sm text-[#D97706] font-medium">{loc.distance.toFixed(1)} miles away</p>
                  )}
                </div>
                <div className="mt-6 flex gap-3">
                  <Link to="/menu" className="flex-1 bg-[#D97706] text-white py-3 rounded-xl font-bold text-center hover:bg-[#B45309] transition-colors flex items-center justify-center gap-2">
                    Order <ArrowRight className="w-4 h-4" />
                  </Link>
                  {loc.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-orange-200 text-[#451A03] font-medium hover:bg-orange-50 transition-colors"
                    >
                      <Navigation className="w-4 h-4" /> Directions
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
